import React from 'react';
import { FormInput, CheckCircle, ArrowRight, AlertTriangle, Target } from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const FormFieldsDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Form Fields Management"
        description="Customize and configure lead form fields to capture exactly the information you need. Create custom fields, dropdowns, and validation rules tailored to your business."
        icon={<FormInput className="w-6 h-6" />}
        badge="Customization"
        badgeColor="green"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* What You'll Learn */}
      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Target className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              What You'll Learn
            </h3>
            <ul className="space-y-1 text-sm text-green-800 dark:text-green-300">
              <li>✓ Creating custom form fields for leads</li>
              <li>✓ Configuring field types (text, dropdown, checkbox, etc.)</li>
              <li>✓ Managing dropdown options and values</li>
              <li>✓ Reordering fields with drag-and-drop</li>
              <li>✓ Setting field visibility and requirements</li>
              <li>✓ Understanding field mapping for integrations</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>What are Form Fields?</h2>
      <p>
        Form Fields define the structure of your lead capture forms. Every piece of information you
        collect about a lead—from name and email to custom attributes like "Budget Range" or
        "Preferred Contact Method"—is configured through Form Fields management.
      </p>

      <h3>Why Customize Form Fields?</h3>
      <ul>
        <li><strong>Capture Relevant Data</strong> - Collect information specific to your business</li>
        <li><strong>Improve Lead Quality</strong> - Require fields that help qualify leads</li>
        <li><strong>Streamline Workflows</strong> - Custom fields enable better automation and reporting</li>
        <li><strong>Integration Mapping</strong> - Match CRM fields to external systems (website, Facebook, etc.)</li>
      </ul>

      <h2>Accessing Form Fields</h2>
      <p>
        Navigate to <code>Settings → CRM Configuration → Form Fields</code>
      </p>

      <h2>Default Lead Fields</h2>
      <p>
        Your CRM comes with pre-configured essential fields:
      </p>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Field Name</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Type</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Purpose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Lead Name</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Text</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Full name of the lead</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Email</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Email</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Primary email address</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Phone</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Phone</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Contact phone number</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Status</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Dropdown</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Current lead status</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Source</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Dropdown</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Lead source/origin</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">City</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Text</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">City location</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Notes</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Textarea</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Additional comments</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Field Types</h2>

      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Text</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Single-line text input. Best for: names, IDs, short descriptions.
          </p>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Textarea</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Multi-line text input. Best for: notes, descriptions, requirements.
          </p>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Dropdown</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Select from predefined options. Best for: status, source, category, priority.
          </p>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Number</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Numeric input only. Best for: budget, quantity, age, years of experience.
          </p>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Email</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Validated email input. Best for: email addresses with automatic format checking.
          </p>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Phone</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Phone number input. Best for: phone numbers with optional country code.
          </p>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Date</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Date picker input. Best for: follow-up dates, deadlines, appointments.
          </p>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Checkbox</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Yes/No toggle. Best for: opt-ins, preferences, boolean conditions.
          </p>
        </div>
      </div>

      <h2>Creating a Custom Field</h2>

      <ol>
        <li>Click "Add Field" button</li>
        <li>Fill in field details:
          <ul>
            <li><strong>Field Name</strong> - Display label (e.g., "Budget Range")</li>
            <li><strong>Field Type</strong> - Select from available types</li>
            <li><strong>Database Column</strong> - Auto-generated identifier</li>
            <li><strong>Required</strong> - Toggle if field must be filled</li>
            <li><strong>Show in Form</strong> - Toggle visibility in lead forms</li>
            <li><strong>Placeholder</strong> - Help text shown in empty field</li>
          </ul>
        </li>
        <li>For dropdown fields, add options (see next section)</li>
        <li>Click "Save" to create the field</li>
      </ol>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Pro Tip:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Use clear, concise field names that your team will understand instantly. Avoid abbreviations
          or technical jargon unless they're standard in your industry.
        </p>
      </div>

      <h2>Managing Dropdown Options</h2>

      <p>
        Dropdown fields (like Status, Source, Priority) require predefined options. Each option has:
      </p>
      <ul>
        <li><strong>Option Name</strong> - Display label shown to users</li>
        <li><strong>Option Value</strong> - Internal identifier (usually same as name)</li>
        <li><strong>Order</strong> - Position in the dropdown list</li>
      </ul>

      <h3>Adding Dropdown Options:</h3>
      <ol>
        <li>Click on a dropdown field (e.g., "Status")</li>
        <li>Scroll to "Dropdown Options" section</li>
        <li>Click "Add Option"</li>
        <li>Enter option name (e.g., "Hot Lead")</li>
        <li>Option value auto-fills (can be customized)</li>
        <li>Set display order</li>
        <li>Click "Save Option"</li>
        <li>Repeat for all needed options</li>
      </ol>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              <strong>⚠️ Caution:</strong>
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Deleting a dropdown option that's used by existing leads may cause data issues.
              Consider marking options as inactive instead of deleting them.
            </p>
          </div>
        </div>
      </div>

      <h2>Reordering Fields</h2>

      <p>
        The order of fields determines how they appear in lead forms, detail views, and reports.
      </p>

      <h3>Drag-and-Drop Reordering:</h3>
      <ol>
        <li>Click and hold the drag handle (⋮⋮) on any field row</li>
        <li>Drag the field to desired position</li>
        <li>Drop to place the field</li>
        <li>Changes save automatically</li>
        <li>Refresh the page to see updated order</li>
      </ol>

      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-6">
        <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
          <strong>📋 Ordering Best Practices:</strong>
        </p>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Place most important fields first (name, email, phone)</li>
          <li>• Group related fields together</li>
          <li>• Put optional fields at the bottom</li>
          <li>• Consider mobile viewing - shorter forms work better</li>
        </ul>
      </div>

      <h2>Field Settings & Properties</h2>

      <h3>Required Fields</h3>
      <p>
        Mark critical fields as "Required" to ensure essential information is collected. Required
        fields have a red asterisk (*) in forms and cannot be submitted empty.
      </p>

      <h3>Show in Form</h3>
      <p>
        Control field visibility. Unchecking "Show in Form" hides the field from:
      </p>
      <ul>
        <li>Website integration forms</li>
        <li>Manual lead creation</li>
        <li>CSV import mapping</li>
      </ul>
      <p>
        Hidden fields can still store data from API integrations or backend processes.
      </p>

      <h3>Field Visibility by User Role</h3>
      <p>
        Some fields may be restricted based on user permissions. Admins see all fields, while
        sales reps may have limited access to sensitive information.
      </p>

      <h2>Integration Field Mapping</h2>

      <p>
        When integrating with external systems (website forms, Facebook Lead Ads, etc.), you'll
        map external fields to your CRM fields:
      </p>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">External Source</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">CRM Field</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Website: full_name</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Lead Name</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Facebook: email</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Email</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Form: phone_number</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Phone</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Custom: budget_range</td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Budget Range (custom field)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Common Use Cases</h2>

      <h3>Real Estate CRM</h3>
      <p>Custom fields to add:</p>
      <ul>
        <li>Property Type (Dropdown: Apartment, Villa, Plot, etc.)</li>
        <li>Budget Range (Dropdown: &lt;50L, 50L-1Cr, 1Cr+)</li>
        <li>Preferred Location (Text)</li>
        <li>Possession Timeline (Dropdown: Immediate, 3 Months, 6 Months, 1 Year)</li>
        <li>Financing Required (Checkbox)</li>
      </ul>

      <h3>B2B SaaS</h3>
      <p>Custom fields to add:</p>
      <ul>
        <li>Company Size (Dropdown: 1-10, 11-50, 51-200, 201-500, 500+)</li>
        <li>Industry (Dropdown: Technology, Finance, Healthcare, etc.)</li>
        <li>Monthly Budget (Number)</li>
        <li>Current Solution (Text)</li>
        <li>Decision Timeline (Dropdown: This Month, This Quarter, Next Quarter)</li>
      </ul>

      <h2>Best Practices</h2>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              Form Fields Best Practices
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Keep forms short</strong> - Only ask for essential information initially</li>
              <li>✓ <strong>Use dropdowns wisely</strong> - Limited options improve data consistency</li>
              <li>✓ <strong>Test before deploying</strong> - Create a test lead to verify field behavior</li>
              <li>✓ <strong>Document custom fields</strong> - Maintain a list of what each field means</li>
              <li>✓ <strong>Regular cleanup</strong> - Remove unused fields periodically</li>
              <li>✓ <strong>Consider mobile</strong> - Ensure fields work well on small screens</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Field not appearing in lead form?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Check:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>"Show in Form" toggle is enabled</li>
              <li>Field order - it may be at the bottom</li>
              <li>Your user role has permission to see the field</li>
              <li>Browser cache - try hard refresh (Ctrl+Shift+R)</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Can't delete a field?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>
              System fields (Name, Email, Phone, Status) cannot be deleted as they're core to CRM
              functionality. For custom fields, ensure they're not referenced in active integrations
              or automation rules before deleting.
            </p>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Dropdown options not saving?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Common causes:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Duplicate option values - each value must be unique</li>
              <li>Missing required field - option name is required</li>
              <li>Session timeout - refresh page and try again</li>
              <li>Browser issue - try different browser</li>
            </ul>
          </div>
        </details>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/integrations/website"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Set Up Website Integration</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Capture leads from your website using custom form fields
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/funnel-management"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Configure Funnel Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Map status field values to funnel stages for analytics
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Form Fields Configured!
            </h3>
            <p className="text-green-800 dark:text-green-300">
              Your lead capture forms are now customized to collect exactly the information you need.
              Remember to keep forms user-friendly and collect only essential data to maximize
              conversion rates.
            </p>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default FormFieldsDocs;
